import React, { useState } from 'react';
import Table, { TFilter } from '@components/table';
import styles from './styles.module.scss';
import Status from '@components/common/status';
import Typography from '@components/common/typography';
import Drawer from '@components/drawer';
import Input from '@components/common/input';
import PriceStatus from '@components/common/price_status';
import Button from '@components/common/button';
import { useTransactions } from '@hooks/api/useTransactions';
import { useParams } from 'react-router-dom';
import {
    ETransactionAction,
    ETransactionMethod,
    ETransactionStatus,
    ETransactionType,
    TTransactionLite
} from '@xeppt/xeppt-sdk/types/transaction';
import {
    getTransactionAction,
    getTransactionMethod,
    getTransactionStatus,
    getTransactionStatusColor
} from '@utils/index';
import moment from 'moment';
import { transactionsMethods, transactionsStatuses, transactionsType } from '@const/index';
import DatePicker from '@components/date_picker';
import Dropdown from '@components/common/dropdown';

const TransactionsSection = () => {
    const { id: customer_id } = useParams<{ id: string }>();
    const {
        onChangeFilter,
        filter,
        transactions,
        refetchTransactions,
        transactionsData,
        transaction,
        getSingleTransaction,
        handleOpenRefundModal,
        handleRemoveTransactionData
    } = useTransactions({ customer_id, isInitialTransactions: true });
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const canBeRefunded = (transaction: TTransactionLite) => {
        const methods = [ETransactionMethod.WALLET];
        const actions = [ETransactionAction.TRANSFER, ETransactionAction.REQUEST];
        return (
            transaction?.status === ETransactionStatus.APPROVED &&
            methods.includes(transaction?.method) &&
            actions.includes(transaction?.action) &&
            transaction?.type === ETransactionType.CREDIT
        );
    };

    const columns = [
        {
            label: 'Date and Time',
            key: 'created_at',
            isSort: true,
            render: (value: string) => moment(value).format('MM.DD.YYYY HH:mm')
        },
        {
            label: 'Transaction ID',
            key: 'id'
        },
        {
            label: 'Transaction Method',
            key: 'method',
            filterOptions: transactionsMethods,
            render: (value: ETransactionMethod) => getTransactionMethod(value) //
        },
        { label: 'Consumer Name', key: 'consumer_name' },
        { label: 'Consumer ID', key: 'consumer_id' },
        {
            label: 'Action',
            key: 'action',
            filterOptions: transactionsType,
            render: (_: number, data: TTransactionLite) => getTransactionAction(data.action)
        },
        {
            label: 'Status',
            key: 'status',
            filterOptions: transactionsStatuses,
            render: (value: ETransactionStatus) => (
                <Status variant={getTransactionStatusColor(value)}>
                    {getTransactionStatus(value)}
                </Status>
            )
        },
        { label: 'Currency', key: 'currency' },
        {
            label: 'Amount',
            key: 'amount',
            render: (value: number, data: TTransactionLite) => (
                <PriceStatus
                    isNegative={data.type === ETransactionType.CREDIT}
                    amount={value + data.fee}
                    currency="$"
                />
            )
        },
        {
            label: 'Actions',
            key: 'id',
            render: (value: string, data: TTransactionLite) => (
                <Dropdown
                    items={[
                        {
                            label: 'Details',
                            onClick: () => {
                                setIsDrawerOpen(true);
                                getSingleTransaction(value);
                            }
                        },
                        canBeRefunded(data) && {
                            label: 'Refund',
                            onClick: () => {
                                handleOpenRefundModal(value);
                            }
                        }
                    ]}
                />
            )
        }
    ];

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <div className={styles.text_wrapper}>
                    <Typography variant="h4">Transactions</Typography>
                </div>
            </div>
            <Table
                //@ts-ignore
                columns={columns}
                rows={transactions}
                sort={{ key: 'created_at', value: filter.order }}
                onSortChange={() => {
                    onChangeFilter({
                        order: filter.order === 'DESC' ? 'ASC' : 'DESC'
                    });
                }}
                filter={filter as TFilter}
                onLoadData={refetchTransactions}
                totalRows={transactionsData?.total || 0}
                onFilterChange={(val) => {
                    console.log(val);
                    onChangeFilter(val);
                }}
                emptyDescription="Transactions list is empty"
            />
            <Drawer
                className={styles.drawer}
                isOpen={isDrawerOpen && !!transaction}
                width={670}
                onClose={() => {
                    setIsDrawerOpen(false);
                    handleRemoveTransactionData();
                }}>
                <Typography className={styles.title} variant="h5">
                    Transaction details
                </Typography>
                <Input full value={transaction?.id} label="Transaction ID" readOnly />
                <div className={styles.row}>
                    <Input full value={transaction?.consumer_id} label="Consumer ID" readOnly />
                    <Input full value={transaction?.consumer_name} label="Consumer Name" readOnly />
                </div>
                <div className={styles.row}>
                    <Input
                        full
                        value={getTransactionMethod(transaction?.method)}
                        label="Method"
                        readOnly
                    />
                    <Input
                        full
                        value={getTransactionAction(transaction?.action)}
                        label="Action"
                        readOnly
                    />
                    <Input
                        full
                        value={getTransactionStatus(transaction?.status)}
                        label="Status"
                        readOnly
                    />
                </div>
                <div className={styles.row}>
                    <Input full value={transaction?.currency} label="Currency" readOnly />
                    <Input full value={transaction?.amount.toString()} label="Amount" readOnly />
                </div>
                <Input
                    full
                    value={transaction?.automatic ? 'Yes' : 'No'}
                    label="Is automatic payment"
                    readOnly
                />
                <div className={styles.row}>
                    {/*//@ts-ignore*/}
                    <Input full value={transaction?.details?.message} label="Message" readOnly />
                </div>
                <div className={styles.row}>
                    <Input full value={transaction?.source} label="Source" readOnly />
                </div>
                <div className={styles.row}>
                    <DatePicker
                        label="Created at"
                        full
                        //@ts-ignore
                        value={transaction?.created_at as string}
                        readOnly
                    />
                </div>
            </Drawer>
        </div>
    );
};

export default TransactionsSection;
